// Commission math. The rule Phillip set: the rep who closes a store gets
// the first month's payment in full, then 10% of every monthly payment
// after that for as long as the store keeps paying. Computed from the
// deal fields on sales_prospects (plan, monthly_amount, billing_start,
// closer_rep_id, churned_at); payouts are the rows in sales_payouts.
// Pure: no React, no Supabase. The setup fee is not commissioned.

export const FIRST_MONTH_RATE = 1; // 100% of the first payment
export const RESIDUAL_RATE = 0.1; // 10% of every payment after

export const PLANS = [
  { key: 'online', label: 'Online Showroom', amount: 299 },
  { key: 'selling', label: 'Selling Showroom', amount: 499 },
  { key: 'receipts', label: 'Digital Receipts', amount: 99 },
  { key: 'phone', label: 'Phone Agent', amount: 99 },
  { key: 'custom', label: 'Custom amount', amount: null },
];
export const PLAN_BY_KEY = Object.fromEntries(PLANS.map((p) => [p.key, p]));
export const planLabel = (key) => PLAN_BY_KEY[key]?.label || (key ? key : 'No plan');

// ---------- dates ----------
const pad = (n) => String(n).padStart(2, '0');
export const periodOf = (d) => { const x = new Date(d); return `${x.getFullYear()}-${pad(x.getMonth() + 1)}`; };
export const periodLabel = (period) => {
  const [y, m] = String(period).split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};
export const todayIso = (now = new Date()) => `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
/** A 'YYYY-MM-DD' date at local noon, so day math never crosses midnight. */
const dateAt = (iso) => {
  if (!iso) return null;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
};
/** The same day n months later; the 31st becomes the last day of a shorter month. */
export const addMonths = (d, n) => {
  const x = new Date(d);
  const day = x.getDate();
  x.setDate(1);
  x.setMonth(x.getMonth() + n);
  const last = new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate();
  x.setDate(Math.min(day, last));
  return x;
};

export const money = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v) % 1 === 0 ? Math.abs(v).toLocaleString() : Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${v < 0 ? '-' : ''}$${s}`;
};

/** True when the row has enough on it to be commissioned. */
export const isDeal = (p) => Number(p?.monthly_amount) > 0 && !!dateAt(p?.billing_start);
/** A customer with no deal fields yet: shows up as "needs the amount". */
export const dealIncomplete = (p) => p?.stage === 'customer' && !isDeal(p);

/**
 * Every payment a deal has made (or will make through `now`), each with
 * the rep's cut. Payment k is made on billing_start + k months; k = 0 is
 * the first month. A churned deal stops at the last payment on or before
 * churned_at.
 */
export function dealPayments(p, now = new Date()) {
  const start = dateAt(p?.billing_start);
  const amount = Number(p?.monthly_amount) || 0;
  if (!start || amount <= 0) return [];
  const end = dateAt(p?.churned_at);
  const cutoff = end && end < now ? end : now;
  const out = [];
  for (let k = 0; k < 1200; k++) {
    const on = addMonths(start, k);
    if (on > cutoff) break;
    const rate = k === 0 ? FIRST_MONTH_RATE : RESIDUAL_RATE;
    out.push({ k, on, period: periodOf(on), paid: amount, commission: Math.round(amount * rate * 100) / 100, first: k === 0 });
  }
  return out;
}

export const isActiveDeal = (p, now = new Date()) => {
  if (!isDeal(p)) return false;
  const end = dateAt(p.churned_at);
  return !end || end > now;
};

/** Totals for one deal. */
export function dealEarnings(p, now = new Date()) {
  const pays = dealPayments(p, now);
  const amount = Number(p?.monthly_amount) || 0;
  const earned = pays.reduce((n, x) => n + x.commission, 0);
  const active = isActiveDeal(p, now);
  return {
    months: pays.length,
    earned: Math.round(earned * 100) / 100,
    residual: active ? Math.round(amount * RESIDUAL_RATE * 100) / 100 : 0, // what it pays the rep every month from here
    nextOn: active && pays.length ? addMonths(dateAt(p.billing_start), pays.length) : null,
    active,
    firstYear: Math.round(amount * (FIRST_MONTH_RATE + 11 * RESIDUAL_RATE) * 100) / 100,
  };
}

/** Which rep a deal pays: the closer if set, else the owner of the row. */
export const closerOf = (p) => p?.closer_rep_id || p?.rep_id || null;

/** Totals per rep across every deal on the list. */
export function repEarnings(prospects, repId, now = new Date()) {
  const deals = prospects.filter((p) => isDeal(p) && closerOf(p) === repId);
  const t = { deals: deals.length, active: 0, earned: 0, residual: 0, thisMonth: 0, firstYear: 0 };
  const cur = periodOf(now);
  for (const p of deals) {
    const e = dealEarnings(p, now);
    t.earned += e.earned;
    t.residual += e.residual;
    t.firstYear += e.firstYear;
    if (e.active) t.active += 1;
    for (const x of dealPayments(p, now)) if (x.period === cur) t.thisMonth += x.commission;
  }
  for (const k of ['earned', 'residual', 'thisMonth', 'firstYear']) t[k] = Math.round(t[k] * 100) / 100;
  return t;
}

/**
 * The statement: one row per rep per billing month with what is owed,
 * the deals behind it, and whether it has been paid (from sales_payouts).
 * Newest month first. Only months with something owed are listed.
 */
export function statement(prospects, reps, payouts, now = new Date()) {
  const byKey = new Map();
  for (const p of prospects) {
    if (!isDeal(p)) continue;
    const repId = closerOf(p);
    if (!repId) continue;
    for (const x of dealPayments(p, now)) {
      const key = `${repId}|${x.period}`;
      let row = byKey.get(key);
      if (!row) { row = { repId, period: x.period, amount: 0, lines: [] }; byKey.set(key, row); }
      row.amount += x.commission;
      row.lines.push({ prospect: p, ...x });
    }
  }
  const paid = new Map((payouts || []).map((q) => [`${q.rep_id}|${q.period}`, q]));
  const repById = Object.fromEntries(reps.map((r) => [r.id, r]));
  const rows = [...byKey.values()].map((r) => ({ ...r, amount: Math.round(r.amount * 100) / 100, rep: repById[r.repId] || null, payout: paid.get(`${r.repId}|${r.period}`) || null }));
  rows.sort((a, b) => b.period.localeCompare(a.period) || (a.rep?.name || '').localeCompare(b.rep?.name || ''));
  return rows;
}

/** Owed and settled totals across a statement. */
export function statementTotals(rows) {
  let owed = 0;
  let settled = 0;
  for (const r of rows) { if (r.payout) settled += r.amount; else owed += r.amount; }
  return { owed: Math.round(owed * 100) / 100, settled: Math.round(settled * 100) / 100 };
}
